import React, { useEffect, useId, useRef, useState } from 'react';

import { useLocation } from 'react-router-dom';
import { NavLink } from '~/lib/react-router-dom';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import {
  ClickAwayListener,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
} from '@mui/material';

import { NAVIGATION_LINK_IDS } from '~/consts/navigation';

import { isNavigationPathSelected } from '~/utils/navigationLinks';
import { openLabPortal } from '~/utils/openLabPortal';

interface Props {
  isExpanded: boolean;
  navLink: App.NavigationLink;
}

const CLOSE_DELAY_MS = 150;

export default function SidebarLink({ isExpanded, navLink }: Props) {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [focusMenu, setFocusMenu] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const menuId = useId();
  const Icon = navLink.icon;
  const childLinks: Array<App.NavigationLink> = navLink.subLinks?.length
    ? navLink.href
      ? [{ href: navLink.href, label: navLink.label }, ...navLink.subLinks]
      : navLink.subLinks
    : [];
  const hasChildren = childLinks.length > 0;
  const isLabLink = navLink.id === NAVIGATION_LINK_IDS.LAB;
  const isSelected =
    isNavigationPathSelected(pathname, navLink.href) ||
    childLinks.some((link) => isNavigationPathSelected(pathname, link.href));

  function cancelClose() {
    clearTimeout(closeTimer.current);
  }

  function closeMenu() {
    cancelClose();
    setIsOpen(false);
    setFocusMenu(false);
  }

  function scheduleClose() {
    cancelClose();
    closeTimer.current = setTimeout(closeMenu, CLOSE_DELAY_MS);
  }

  function openMenu(keyboard = false) {
    cancelClose();
    setFocusMenu(keyboard);
    setIsOpen(true);
  }

  function handleBlur(event: React.FocusEvent) {
    const target = event.relatedTarget as Node | null;
    if (
      !triggerRef.current?.contains(target) &&
      !panelRef.current?.contains(target) &&
      !(target instanceof Element && target.closest('[role="menu"]')?.id === menuId)
    ) {
      closeMenu();
    }
  }

  useEffect(() => {
    setIsOpen(false);
    setFocusMenu(false);
    clearTimeout(closeTimer.current);
  }, [pathname, isExpanded]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <Tooltip title={isExpanded || hasChildren ? '' : navLink.label} placement="right">
        <ListItemButton
          ref={triggerRef}
          component={hasChildren || isLabLink ? 'button' : NavLink}
          to={hasChildren || isLabLink ? undefined : navLink.href}
          aria-label={navLink.label}
          aria-haspopup={hasChildren ? 'menu' : undefined}
          aria-expanded={hasChildren ? isOpen : undefined}
          aria-controls={isOpen ? menuId : undefined}
          selected={isSelected}
          onMouseEnter={() => {
            if (hasChildren) {
              openMenu();
            }
          }}
          onMouseLeave={() => {
            if (hasChildren && !focusMenu) {
              scheduleClose();
            }
          }}
          onBlur={handleBlur}
          onClick={(event) => {
            if (isLabLink) {
              event.preventDefault();
              void openLabPortal();
            } else if (hasChildren) {
              event.preventDefault();
              openMenu(true);
            }
          }}
          onKeyDown={(event) => {
            if (hasChildren && (event.key === 'ArrowRight' || event.key === 'ArrowDown')) {
              event.preventDefault();
              openMenu(true);
            } else if (event.key === 'Escape') {
              closeMenu();
            }
          }}
          sx={{
            minHeight: 48,
            justifyContent: isExpanded ? 'initial' : 'center',
            px: 2,
            textAlign: 'left',
            width: '100%',
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', justifyContent: 'center', minWidth: isExpanded ? 40 : 0 }}>
            {Icon ? <Icon /> : <MenuIcon />}
          </ListItemIcon>
          {isExpanded && <ListItemText primary={navLink.label} />}
          {isExpanded && hasChildren && <ChevronRightIcon sx={{ ml: 'auto' }} />}
        </ListItemButton>
      </Tooltip>
      {hasChildren && (
        <Popper
          open={isOpen}
          anchorEl={triggerRef.current}
          placement="right-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal }}
        >
          <ClickAwayListener onClickAway={closeMenu}>
            <Paper
              ref={panelRef}
              elevation={8}
              onMouseEnter={cancelClose}
              onMouseLeave={() => {
                if (!focusMenu) {
                  scheduleClose();
                }
              }}
              onBlur={handleBlur}
              sx={{
                minWidth: 200,
                maxWidth: 'min(360px, calc(100vw - 32px))',
                maxHeight: 'calc(100vh - 32px)',
                overflowY: 'auto',
              }}
            >
              <MenuList
                id={menuId}
                aria-label={navLink.label}
                autoFocusItem={focusMenu}
                onKeyDown={(event) => {
                  if (event.key === 'Escape' || event.key === 'ArrowLeft') {
                    event.preventDefault();
                    event.stopPropagation();
                    closeMenu();
                    triggerRef.current?.focus();
                  }
                }}
              >
                {childLinks.flatMap((link, index) => {
                  const isNewSection = link.section && (index === 0 || childLinks[index - 1].section !== link.section);

                  return [
                    ...(isNewSection && index > 0 ? [<Divider key={`divider-${index}`} />] : []),
                    ...(isNewSection
                      ? [
                          <ListSubheader key={`section-${index}`} disableSticky>
                            {link.section}
                          </ListSubheader>,
                        ]
                      : []),
                    <MenuItem
                      key={`${navLink.label}-${link.href}`}
                      component={NavLink}
                      to={link.href}
                      selected={isNavigationPathSelected(pathname, link.href)}
                      onClick={closeMenu}
                      sx={{ whiteSpace: 'normal' }}
                    >
                      {link.label}
                    </MenuItem>,
                  ];
                })}
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </ListItem>
  );
}
